/* My access as an explorer (Tony, 21 Oct 2026): a folder tree like Drive's sidebar, with the
   selected folder on the right. Turns the API's flat per-drive list (each folder carrying its
   `path`) into one tree per drive:
     - every folder the person can open is a node;
     - every level above one that they cannot open is a pass-through node (grey, no link);
     - nothing else: locked folders that lead nowhere the person can go never appear.
   Pure, so it is tested without a browser (myAccessTree.test.ts). */

import type { AccessDrive, AccessFolder } from '../types'

export interface DriveModel {
  driveName: string
  confidentiality: AccessDrive['confidentiality']
  /** HUB, GROUP, KE… for the country switcher and the drive row. */
  code: string
}

export interface ExplorerNode {
  /** Folder ID, or `drive:<name>` for a drive. */
  id: string
  name: string
  kind: 'drive' | 'folder'
  drive: DriveModel
  /** The folder as the API returned it. Absent on drives and pass-through levels. */
  folder?: AccessFolder
  /** A level the person passes through but cannot open. */
  pass: boolean
  parent: ExplorerNode | null
  children: ExplorerNode[]
}

/** " · KE": the entity suffix on department folder names (mirrors ENTITY_SUFFIX in the API). */
const ENTITY_SUFFIX = / · [A-Za-z]+$/
export const baseName = (name: string) => name.replace(ENTITY_SUFFIX, '')

export function driveCode(driveName: string, entityCode?: string | null): string {
  if (entityCode) return entityCode.toUpperCase()
  if (/company hub/i.test(driveName)) return 'HUB'
  if (/^\d+ RMS Group$/i.test(driveName)) return 'GROUP'
  const m = /^\d+ RMS ([A-Z]{2}) /.exec(driveName)
  return m ? m[1] : driveName.replace(/^\d+\s+/, '').slice(0, 6).toUpperCase()
}

export const driveNodeId = (driveName: string) => `drive:${driveName}`

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, undefined, { numeric: true })

function sortTree(nodes: ExplorerNode[]) {
  nodes.sort(byName)
  for (const n of nodes) sortTree(n.children)
}

/** My access plus the live check's provisional folders: same-name drives joined, folders the page
    already lists kept as they are, new drives after the rest. */
export function withLive(drives: AccessDrive[], live: AccessDrive[] | null | undefined): AccessDrive[] {
  if (!live?.length) return drives
  const listed = new Set(drives.flatMap((d) => d.folders.map((f) => f.folderId)))
  const out = drives.map((d) => ({ ...d, folders: [...d.folders] }))
  for (const ld of live) {
    const fresh = ld.folders.filter((f) => !listed.has(f.folderId))
    if (!fresh.length) continue
    const same = out.find((d) => d.driveName === ld.driveName)
    if (same) same.folders.push(...fresh)
    else out.push({ ...ld, folders: fresh })
  }
  return out
}

/** One root per drive, in API order; the same drive arriving twice (old API) is folded. */
export function buildExplorer(drives: AccessDrive[]): ExplorerNode[] {
  const roots: ExplorerNode[] = []
  const nodes = new Map<string, ExplorerNode>()

  for (const d of drives) {
    const listed = d.folders.filter((f) => f.state !== 'not-visible')
    if (!listed.length) continue
    let root = nodes.get(driveNodeId(d.driveName))
    if (!root) {
      const model: DriveModel = {
        driveName: d.driveName,
        confidentiality: d.confidentiality,
        code: driveCode(d.driveName, listed.find((f) => f.entityCode)?.entityCode),
      }
      root = { id: driveNodeId(d.driveName), name: d.driveName, kind: 'drive', drive: model, pass: false, parent: null, children: [] }
      nodes.set(root.id, root)
      roots.push(root)
    }
    for (const f of listed) {
      let parent = root
      for (const seg of f.path ?? []) {
        let n = nodes.get(seg.folderId)
        if (!n) {
          n = { id: seg.folderId, name: seg.name, kind: 'folder', drive: root.drive, pass: true, parent, children: [] }
          nodes.set(n.id, n)
          parent.children.push(n)
        }
        parent = n
      }
      let own = nodes.get(f.folderId)
      if (!own) {
        own = { id: f.folderId, name: f.name, kind: 'folder', drive: root.drive, pass: false, parent, children: [] }
        nodes.set(own.id, own)
        parent.children.push(own)
      }
      // A node first seen as someone's ancestor becomes a real folder once its own entry arrives.
      own.folder = f
      own.pass = false
      own.name = f.name
    }
  }
  for (const r of roots) sortTree(r.children)
  return roots
}

export function indexTree(roots: ExplorerNode[]): Map<string, ExplorerNode> {
  const index = new Map<string, ExplorerNode>()
  const walk = (n: ExplorerNode) => { index.set(n.id, n); n.children.forEach(walk) }
  roots.forEach(walk)
  return index
}

/** Ancestors of a node, root first, excluding the node itself. */
export function ancestors(node: ExplorerNode): ExplorerNode[] {
  const out: ExplorerNode[] = []
  for (let p = node.parent; p; p = p.parent) out.unshift(p)
  return out
}

/** Every drive code the person reaches, in order, for the country switcher. */
export function driveCodes(roots: ExplorerNode[]): string[] {
  return [...new Set(roots.map((r) => r.drive.code))]
}

/** How many folders under a node the person can open. */
export const openCount = (n: ExplorerNode): number =>
  n.children.reduce((c, x) => c + (x.folder && x.folder.state === 'visible' ? 1 : 0) + openCount(x), 0)

/** The description line, unless it only repeats the folder's name ("03 FP&A" / "FP&A"). */
export function usefulDescription(f: AccessFolder): string | undefined {
  const what = f.whatBelongsHere?.trim()
  if (!what) return undefined
  const plain = (s: string) => baseName(s).replace(/^\d+\s+/, '').trim().toLowerCase()
  return plain(what) === plain(f.name) ? undefined : what
}

/** Case-insensitive name filter: matching nodes plus their ancestors, as a set of IDs to show. */
export function filterIds(roots: ExplorerNode[], query: string): Set<string> | null {
  const q = query.trim().toLowerCase()
  if (!q) return null
  const keep = new Set<string>()
  const walk = (n: ExplorerNode): boolean => {
    const childHit = n.children.map(walk).some(Boolean)
    const hit = n.kind === 'folder' && n.name.toLowerCase().includes(q)
    if (hit || childHit) keep.add(n.id)
    return hit || childHit
  }
  roots.forEach(walk)
  return keep
}
